import { Combobox, Input, InputBase, Loader, useCombobox } from "@mantine/core";
import { DefaultError, QueryKey, useInfiniteQuery, UseSuspenseInfiniteQueryOptions } from "@tanstack/react-query";
import { ReactNode, useState } from "react";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";

type InfiniteQuerySelectProps<TItem extends object, TQueryFnData, TData extends { pages: TItem[][] }, TQueryKey extends QueryKey> = {
    initialValue?: string | undefined;
    onChange?: (nextItemIdentity: string | undefined, item: TItem | undefined) => void;
    query: UseSuspenseInfiniteQueryOptions<TQueryFnData, DefaultError, TData, TQueryFnData, TQueryKey, number>;
    toIdentity: (item: NoInfer<TItem>) => string;
    render: (item: NoInfer<TItem>) => ReactNode;
};

export const InfiniteQuerySelect = <
    TItem extends object,
    TQueryFnData extends TItem[],
    TData extends { pages: TItem[][] },
    TQueryKey extends QueryKey,
>(
    props: InfiniteQuerySelectProps<TItem, TQueryFnData, TData, TQueryKey>,
) => {
    const [value, setValue] = useState<string | null>(props.initialValue ?? null);
    const { data, isLoading } = useInfiniteQuery(props.query);
    const items = data?.pages.flat() ?? [];

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const options = items.map((item) => (
        <Combobox.Option value={props.toIdentity(item)} key={props.toIdentity(item)}>
            {props.render(item)}
        </Combobox.Option>
    ));

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={(val) => {
                setValue(val);
                props.onChange?.(
                    val,
                    items.find((it) => props.toIdentity(it) == val),
                );
                combobox.closeDropdown();
            }}
        >
            <Combobox.Target>
                <InputBase
                    component="button"
                    type="button"
                    pointer
                    rightSection={isLoading ? <Loader size={18} /> : <Combobox.Chevron />}
                    onClick={() => combobox.toggleDropdown()}
                    rightSectionPointerEvents="none"
                >
                    {value || <Input.Placeholder>Select...</Input.Placeholder>}
                </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options mah={200} style={{ overflowY: "auto" }}>
                    {isLoading ? <Combobox.Empty>Loading....</Combobox.Empty> : options}

                    <LoadMoreTrigger query={props.query} />
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};
